import { getMailCreatorAccountIds, getUnreadThreadMessageCountByIds, listUnreadMail, listSentMailNotDeleted} from "src/aws/customAPI"
import { execApi } from "src/apis/execApi"

type Params = {
  accessToken: string
  accountId: string
  organizationId: string
  teamMemberId:string
}

export type UnreadResponse = {
  inbox: boolean
  outbox: boolean
}

export const hasUnreadThreadMessage = async ({accessToken, accountId, organizationId ,teamMemberId}: Params): Promise<UnreadResponse> => {
  /**
   *  accountId: ログインしているユーザーのアカウントID
   *  organizationId: ユーザーが選択している団体のID
   *  teamMemberId: ユーザーが選択している団体におけるユーザーのID(チームメンバーID)
   *
   *  1. 未読のスレッド一覧を取得: 未読のmailIdsを取得
   *  2. 未読のスレッド一覧に対して、メールの作成者の`accountId`を取得
   *  3. メール作成者が自分の場合は`outbox`、他人の場合は`inbox` に未読のメールがあることを判定
   *  4. outboxの場合、送信者として削除していない連絡のみをカウント
   */

  const result = await execApi(
    () => getUnreadThreadMessageCountByIds(accessToken, {
      accountId,
      teamId: organizationId,
      aggrUnit: "account"
    }),
    res => res.searchThreadMessages.aggregateItems[0].result,
  )

  if (!result.isOk || result.content.buckets.length === 0) {
    return {
      inbox: false,
      outbox: false
    }
  }

  const mailIds = new Set<string>()
  result.content.buckets.every(doc => mailIds.add(doc.key))
  const mailIDs = result.content.buckets.map(bucket => ({
    mailID: {
      eq: bucket.key
    }
  }));

  const result2 = await execApi(
    () => listUnreadMail(accessToken, {
      teamMemberId,
      mailIDs,
    }),
    res => res.searchTeamMemberMails
  )

  if(result2.isOk && result2.content.total=== 0) {
    return {
      inbox: false,
      outbox: false
    }
  }

  const mailIdsArray = Array.from(mailIds)

  const mailCreatorAccountIds = await execApi(() => getMailCreatorAccountIds(accessToken, { mailIds: mailIdsArray }), res => res.getMailCreatorAccountIds.mailCreatorAccountIds)

  if (mailCreatorAccountIds.isOk) {
    const hasInbox = mailCreatorAccountIds.content.some(item => item !== accountId)

    // 自分が送信した連絡のmailIdを特定
    // mailCreatorAccountIdsはmailIdsArrayと同じ順序で返される
    const sentMailIds = mailIdsArray.filter((_, index) => mailCreatorAccountIds.content[index] === accountId)

    if (sentMailIds.length === 0) {
      // 自分が送信した連絡がない場合はoutboxはfalse
      return {
        inbox: hasInbox,
        outbox: false
      }
    }

    // 自分が送信した連絡のmailIDフィルタを作成
    const sentMailIDFilters = sentMailIds.map(mailId => ({
      mailID: {
        eq: mailId
      }
    }))

    // 送信者として削除していない連絡があるかチェック
    const sentMailResult = await execApi(
      () => listSentMailNotDeleted(accessToken, {
        teamMemberId,
        mailIDs: sentMailIDFilters,
      }),
      res => res.searchTeamMemberMails
    )

    // クライアント側でsentMailIdsに含まれる連絡があるかチェック
    // Note: listSentMailNotDeletedはhasThreadフィルタを含まないため、クライアント側でフィルタリング
    const sentMailIdSet = new Set(sentMailIds)
    let hasOutbox: boolean
    if (sentMailResult.isOk) {
      hasOutbox = sentMailResult.content.items?.some((item: { mail?: { id?: string } }) =>
        item.mail?.id && sentMailIdSet.has(item.mail.id)
      ) ?? false
    } else {
      // APIがエラーの場合: v2.5.3と同じロジックにフォールバック
      // 送信した連絡があればtrue（送信者として削除した連絡も含む可能性があるが、エラー時のフォールバックとして許容）
      // Note: senderIdフィルタ追加により401エラーは解消されたため、通常このフォールバックは実行されない
      // console.warn('[hasUnreadThreadMessage] listSentMailNotDeleted API error:', JSON.stringify(sentMailResult.error))
      // hasOutbox = sentMailIds.length > 0
      hasOutbox = false
    }

    return {
      inbox: hasInbox,
      outbox: hasOutbox
    }
  }
  return {
    inbox: false,
    outbox: false
  }
}
